/**
 * Transform Session Achievement Unlocks to SessionAchievementDisplay format
 * Uses BE AchievementType enum directly (no mapping to FE types)
 */

import { SessionAchievementUnlock } from 'pure-js/types/Session'
import { AchievementType as BEAchievementType, AchievementTier } from 'pure-js/types/Achievement'
import { AchievementLevel, SessionAchievementDisplay } from '../types/achievementTypes'
import { getAchievementText } from '../constants/achievementTextMapping'

/**
 * Map BE tier to frontend AchievementLevel
 */
function mapTierToLevel(tier: string): AchievementLevel {
  switch (tier.toLowerCase()) {
    case 'gold':
      return AchievementLevel.GOLD
    case 'silver':
      return AchievementLevel.SILVER
    case 'bronze':
      return AchievementLevel.BRONZE
    default:
      return AchievementLevel.BRONZE
  }
}

/**
 * Type guard to validate achievementId is a valid BE AchievementType
 */
function isValidAchievementType(id: string): id is BEAchievementType {
  return Object.values(BEAchievementType).includes(id as BEAchievementType)
}

/**
 * Type guard to validate tier is a valid BE AchievementTier
 */
function isValidAchievementTier(tier: string): tier is AchievementTier {
  return Object.values(AchievementTier).includes(tier as AchievementTier)
}

/**
 * Transform session achievement unlock to SessionAchievementDisplay format
 * Uses BE AchievementType directly with text mapping
 *
 * @param unlock - Session achievement unlock from BE
 * @returns SessionAchievementDisplay object or null if invalid
 */
export function transformSessionAchievementToDisplay(
  unlock: SessionAchievementUnlock
): SessionAchievementDisplay | null {
  // Validate achievementId is a valid BE AchievementType
  if (!isValidAchievementType(unlock.achievementId)) {
    console.warn(`Invalid achievement ID: ${unlock.achievementId}`)
    return null
  }

  // Validate tier is a valid BE AchievementTier
  if (!isValidAchievementTier(unlock.tier)) {
    console.warn(`Invalid achievement tier: ${unlock.tier}`)
    return null
  }

  // Map tier to level
  const level = mapTierToLevel(unlock.tier)

  // Get text mapping for this achievement type
  const textMapping = getAchievementText(unlock.achievementId as BEAchievementType)

  return {
    id: unlock.achievementId as BEAchievementType,
    tier: level,
    title: textMapping.title,
    tooltip: textMapping.tooltip,
    description: textMapping.description
  }
}

/**
 * Transform array of session achievements, keeping only highest tier per achievementId
 *
 * @param achievementsUnlocked - Array of session achievements from BE
 * @returns Array of SessionAchievementDisplay objects
 */
export function transformSessionAchievementsToDisplay(
  achievementsUnlocked: SessionAchievementUnlock[]
): SessionAchievementDisplay[] {
  if (!achievementsUnlocked || achievementsUnlocked.length === 0) {
    return []
  }

  // Group by achievementId and keep highest tier
  const achievementMap = new Map<string, SessionAchievementUnlock>()
  const tierPriority: Record<string, number> = {
    gold: 3,
    silver: 2,
    bronze: 1
  }

  achievementsUnlocked.forEach((achievement) => {
    const existing = achievementMap.get(achievement.achievementId)
    const currentPriority = tierPriority[achievement.tier.toLowerCase()] || 0
    const existingPriority = existing ? tierPriority[existing.tier.toLowerCase()] || 0 : 0

    if (!existing || currentPriority > existingPriority) {
      achievementMap.set(achievement.achievementId, achievement)
    }
  })

  // Transform to SessionAchievementDisplay format
  const achievements: SessionAchievementDisplay[] = []
  achievementMap.forEach((unlock) => {
    const achievement = transformSessionAchievementToDisplay(unlock)
    if (achievement) {
      achievements.push(achievement)
    }
  })

  return achievements
}
